import { useEffect, useRef, useState } from "react";

type Page<T> = {
    rows: T[]
    total: number
}

type Options<T> = {
    storeId: number
    pageSize: number
    maxSelected: number
    selectionLimitNotice: string
    fetchPage: (page: number, pageSize: number, search: string, status: string) => Promise<Page<T>>
    isSyncing?: boolean
    onForceSyncSelection?: (entityIds: number[]) => Promise<string>
}

export function useReportPage<T extends { id: number }>({
    storeId,
    pageSize,
    maxSelected,
    selectionLimitNotice,
    fetchPage,
    isSyncing,
    onForceSyncSelection,
}: Options<T>) {
    const [rows, setRows] = useState<T[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [isLoading, setLoading] = useState(true);
    const [retrieveError, setRetrieveError] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState<string>("");
    const [status, setStatus] = useState<string>("");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
    const [refreshCount, setRefreshCount] = useState(0);

    const fetchPageRef = useRef(fetchPage);
    fetchPageRef.current = fetchPage;

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setRetrieveError(false);
            try {
                const page = await fetchPageRef.current(currentPage, pageSize, search, status);
                if (cancelled) return;
                setRows(page.rows);
                setTotal(page.total);
            }
            catch {
                if (cancelled) return;
                setRows([]);
                setTotal(0);
                setRetrieveError(true);
            }
            finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => { cancelled = true; };
    }, [storeId, currentPage, pageSize, search, status, refreshCount]);

    const refresh = () => setRefreshCount(count => count + 1);

    const wasSyncing = useRef(false);

    useEffect(() => {
        if (wasSyncing.current && !isSyncing) refresh();
        wasSyncing.current = !!isSyncing;
    }, [isSyncing]);

    const updateSearch = (value: string) => {
        setCurrentPage(1);
        setSearch(value);
    };

    const updateStatus = (value: string) => {
        setCurrentPage(1);
        setStatus(value);
    };

    const clearFilters = () => {
        setCurrentPage(1);
        setSearch("");
        setStatus("");
    };

    const selectionFull = selectedIds.length >= maxSelected;

    const toggleSelected = (id: number) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(ids => ids.filter(selected => selected !== id));
            return;
        }

        if (selectionFull) {
            setSelectionNotice(selectionLimitNotice);
            return;
        }

        setSelectedIds(ids => [...ids, id]);
    };

    const allOnPageSelected = rows.length > 0 && rows.every(row => selectedIds.includes(row.id));

    const toggleAllOnPage = () => {
        const pageIds = rows.map(row => row.id);

        if (allOnPageSelected) {
            setSelectedIds(ids => ids.filter(id => !pageIds.includes(id)));
            return;
        }

        const missing = pageIds.filter(id => !selectedIds.includes(id));
        const room = maxSelected - selectedIds.length;

        if (missing.length > room) setSelectionNotice(selectionLimitNotice);

        setSelectedIds(ids => [...ids, ...missing.slice(0, room)]);
    };

    const forceSyncSelection = async (): Promise<string> => {
        if (!onForceSyncSelection) return "";

        const message = await onForceSyncSelection(selectedIds);
        if (!message) setSelectedIds([]);

        return message;
    };

    const toggleDetails = (id: number) => setExpandedId(current => current === id ? null : id);

    return {
        rows, total, isLoading, retrieveError,
        currentPage, setCurrentPage,
        search, updateSearch,
        status, updateStatus,
        clearFilters,
        refresh,
        selectedIds, toggleSelected, allOnPageSelected, toggleAllOnPage,
        clearSelection: () => setSelectedIds([]),
        forceSyncSelection,
        expandedId, toggleDetails,
        selectionNotice, dismissNotice: () => setSelectionNotice(null),
    };
}
